import React from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import ProfileSection from 'app/common/pixel-menu/_comp/ProfileSection'
import ArrowRightFromBracketIcon from 'client/utils/icons/arrow-right-from-bracket-icon'
import moment from 'moment'
import {
  DcBreadcrumbs,
  DcButton,
  DcDateRangePicker,
  DcDiv,
  DcFlexBox,
  PixelCurrency,
  PixelDate,
  PixelPageContainer,
  PixelTable,
  PixelTooltip,
  Section,
  SectionActions,
  SectionContent,
  colors,
} from 'pixel-base'
import queryString from 'query-string'
import styled from 'styled-components'

import { getReturnBatches } from 'utils/api-routes.util'

const breadcrumbs = [
  {
    name: 'Orders',
    active: false,
    link: '/orders',
  },
  {
    name: 'Returns Dashboard',
    active: false,
    link: '/orders/returns',
  },
  {
    name: 'Received Returns Batches',
    active: true,
  },
]
function convertToCSV(arr) {
  const header = Object.keys(arr[0]).join(',')
  const rows = arr.map((obj) => Object.values(obj).join(','))
  return `${header}\n${rows.join('\n')}`
}
function downloadCSV(csv) {
  const csvData = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const csvURL = URL.createObjectURL(csvData)
  const tempLink = document.createElement('a')
  tempLink.href = csvURL
  tempLink.setAttribute('download', 'Received Returns Batches.csv')
  tempLink.click()
}
const TruncatedShipperName: React.FC<{ name?: string | null }> = ({ name }) => {
  const textRef = React.useRef<HTMLSpanElement>(null)
  const [isTruncated, setIsTruncated] = React.useState(false)
  const displayName = name?.trim() ? name : '--'

  const checkTruncation = React.useCallback(() => {
    const element = textRef.current
    if (!element) return
    const truncated = element.scrollWidth > element.clientWidth
    setIsTruncated((previous) =>
      previous === truncated ? previous : truncated,
    )
  }, [])

  React.useLayoutEffect(() => {
    checkTruncation()
  }, [checkTruncation, displayName])

  React.useEffect(() => {
    const element = textRef.current
    if (!element || typeof ResizeObserver === 'undefined') {
      return undefined
    }

    const observer = new ResizeObserver(() => {
      checkTruncation()
    })
    observer.observe(element)
    if (element.parentElement) {
      observer.observe(element.parentElement)
    }

    return () => {
      observer.disconnect()
    }
  }, [checkTruncation, displayName])

  return (
    <ShipperName>
      <ShipperNameText ref={textRef}>{displayName}</ShipperNameText>
      {isTruncated && displayName !== '--' && (
        <PixelTooltip
          tooltipText={displayName}
          tooltipPlacement="top"
          width="100%"
          style={{
            position: 'absolute',
            inset: 0,
            minWidth: 0,
            maxWidth: '100%',
            height: '100%',
          }}
        >
          <TooltipHitTarget tabIndex={0} aria-label={displayName} />
        </PixelTooltip>
      )}
    </ShipperName>
  )
}

const columns = [
  {
    dataKey: 'courier_name',
    name: 'Shipper',
    sortable: true,
    width: '20%',
    selector: (row) => (
      <ShipperCell>
        <ShipperImage src={row.courier_logo} alt="" />
        <TruncatedShipperName name={row.courier_name} />
      </ShipperCell>
    ),
  },
  {
    dataKey: 'date_id',
    name: 'Received Date',
    sortable: true,
    width: '15%',
    selector: (row) => (
      <PixelDate
        className="textSmall"
        value={row?.date_id}
        format={'pixelStandard'}
      />
    ),
  },
  {
    dataKey: 'return_batch_id',
    name: 'Batch ID',
    sortable: true,
    selector: (row) => row.dispatch_id,
  },
  {
    dataKey: 'total_order_count',
    name: 'Total Orders',
    sortable: true,
    selector: (row) => row.total_order_count,
  },
  {
    dataKey: 'total_item_qty',
    name: 'Total Items',
    sortable: true,
    selector: (row) => row.total_item_qty,
  },
  {
    dataKey: 'unique_item_count',
    name: 'Unique Items',
    sortable: true,
    selector: (row) => row.unique_item_count,
  },
  {
    dataKey: 'total_amount',
    name: 'Total Amount',
    sortable: true,
    minWidth: '140px',
    selector: (row) => (
      <PixelCurrency
        className="textSmall"
        value={row.total_amount}
        format={'pixelStandard'}
      />
    ),
  },
]

const Received = () => {
  const navigate = useNavigate()

  const [data, setData] = React.useState([])
  const currentDate = new Date()
  const start_date = new Date(currentDate)
  start_date.setDate(currentDate.getDate() - 29)
  const end_date = new Date(currentDate)
  const [searchParams] = useSearchParams()
  const [tableLoading, setTableLoading] = React.useState(false)
  const [selectedRows, setSelectedRows] = React.useState([])
  const [selectedDateRange, setSelectedDateRange] = React.useState({
    startDate: start_date,
    endDate: end_date,
  })

  const [filters, setFilters] = React.useState<any>({
    page: 0,
    search: '',
    type: 'returning',
    start_date: '',
    end_date: '',
  })
  const [totalRows, setTotalRows] = React.useState(0)

  React.useEffect(() => {
    getOrders()
  }, [filters])

  React.useEffect(() => {
    const params: any = searchParams
    if (params.search) setFilters(params)
  }, [])

  const getOrders = () => {
    setTableLoading(true)
    getReturnBatches(filters).subscribe((res: any) => {
      setData(res.response?.data || [])
      setTotalRows(res.response?._meta?.total || 0)
      setTableLoading(false)
    })
  }

  const handlePageChange = (page) => {
    const updatedFilters = {
      ...filters,
      page: page - 1,
      start_date: moment(selectedDateRange.startDate).format('YYYY-MM-DD'),
      end_date: moment(selectedDateRange.endDate).format('YYYY-MM-DD'),
    }
    setFilters(updatedFilters)
  }
  const onRowClicked = (row) => {
    navigate(
      `/orders/returns/received/${row.return_batch_id}/${row.courier_id}`,
    )
  }
  const handleSelectedRowsChange = (rows) => {
    setSelectedRows(rows.selectedRows)
  }

  const exportSelectedRows = () => {
    const csv = convertToCSV(selectedRows.length ? selectedRows : data)
    downloadCSV(csv)
  }
  const handleDateRangeApply = (dateRange) => {
    if (dateRange) {
      const { startDate, endDate } = dateRange
      const formattedStartDate = moment(startDate).format('YYYY-MM-DD')
      const formattedEndDate = moment(endDate).format('YYYY-MM-DD')

      const updatedFilters = {
        ...filters,
        start_date: formattedStartDate,
        end_date: formattedEndDate,
        page: 0,
      }

      navigate({
        pathname: '/orders/returns/received',
        search: queryString.stringify(updatedFilters),
      })
      setFilters(updatedFilters)
    }
  }
  const subHeaderComponentMemo = React.useMemo(() => {
    return (
      <SectionActions
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginBottom: '10px',
        }}
      >
        <DcDateRangePicker
          month={2}
          ranges={{
            start_date: new Date(selectedDateRange.startDate),
            end_date: new Date(selectedDateRange.endDate),
          }}
          onApply={() => handleDateRangeApply(selectedDateRange)}
          onChange={(e) => {
            setSelectedDateRange({
              ...selectedDateRange,
              startDate: e.range1.startDate,
              endDate: e.range1.endDate,
            })
          }}
        />
        &nbsp; &nbsp;
        <DcButton variant="outline-primary" onClick={exportSelectedRows}>
          Export
        </DcButton>
      </SectionActions>
    )
  }, [
    filters,
    selectedDateRange,
    setSelectedDateRange,
    exportSelectedRows,
    handleDateRangeApply,
  ])
  return (
    <DcFlexBox flexDirection="column">
      <DcFlexBox
        padding="0px 20px"
        height="60px"
        style={{
          justifyContent: 'space-between',
          borderBottom: `1px solid ${colors.grayBorderLight}`,
        }}
        alignItems="center"
      >
        <DcFlexBox alignItems="center" width="auto">
          <DcBreadcrumbs fontSize="16px" data={breadcrumbs} />
          <SectionActions />
        </DcFlexBox>
        <DcDiv>
          <ProfileSection />
        </DcDiv>
      </DcFlexBox>

      <PixelPageContainer
        style={{ padding: '0px', height: 'calc(100vh - 60px)' }}
      >
        <Section style={{ padding: '0px' }}>
          <SectionContent style={{ padding: '0px', borderRadius: '0px' }}>
            <SectionActions
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                padding: '5px 20px 5px 10px',
              }}
            >
              <DcDateRangePicker
                size="sm"
                month={2}
                ranges={{
                  start_date: new Date(selectedDateRange.startDate),
                  end_date: new Date(selectedDateRange.endDate),
                }}
                onApply={() => handleDateRangeApply(selectedDateRange)}
                onChange={(e) => {
                  setSelectedDateRange({
                    ...selectedDateRange,
                    startDate: e.range1.startDate,
                    endDate: e.range1.endDate,
                  })
                }}
              />
              &nbsp;
              <DcButton
                size="sm"
                variant="outline-secondary"
                onClick={exportSelectedRows}
                withIcon={true}
              >
                <ArrowRightFromBracketIcon size={16} />
                <span>Export</span>
              </DcButton>
            </SectionActions>
          </SectionContent>
        </Section>
        <Section style={{ padding: '20px' }}>
          {' '}
          <SectionContent>
            <PixelTable
              fixedHeader
              fixedHeaderScrollHeight="calc(100vh - 216px)"
              pagination={true}
              columns={columns}
              progressPending={tableLoading}
              data={data || []}
              subHeaderComponent={subHeaderComponentMemo}
              paginationServer
              paginationTotalRows={totalRows}
              highlightOnHover={true}
              selectableRows
              selectableRowsHighlight
              onSelectedRowsChange={handleSelectedRowsChange}
              isSearchable={false}
              pointerOnHover={true}
              paginationPerPage={50}
              onChangePage={handlePageChange}
              onRowClicked={onRowClicked}
            />
          </SectionContent>
        </Section>
      </PixelPageContainer>
    </DcFlexBox>
  )
}

const ShipperCell = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 16px;
  right: 16px;
  display: flex;
  align-items: center;
  min-width: 0;
  width: auto;
  max-width: 100%;
  overflow: hidden;
  box-sizing: border-box;
`

const ShipperImage = styled.img`
  width: 30px;
  height: 30px;
  flex-shrink: 0;
  border-radius: 50%;
  margin-right: 10px;
`

const ShipperName = styled.div`
  position: relative;
  flex: 1 1 0%;
  min-width: 0;
  width: 100%;
  overflow: hidden;
`

const ShipperNameText = styled.span`
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const TooltipHitTarget = styled.span`
  position: absolute;
  inset: 0;
  display: block;
  cursor: default;
`

export default Received
